/**
 * Sofa Configuration URL — shareable sofa-builder links.
 *
 * Read side:  parses URL params, clamps them against window.sofaSliderData,
 *             mutates defaultDimensions BEFORE the React slider mounts, and
 *             applies fabric/firmness/legs/corner via the existing
 *             EnaviSofaBuilder selection handlers.
 * Write side: "Copy configuration link" button serializes the cached
 *             sofa:configuration:changed detail + active DOM selections.
 *
 * Pure helpers are exposed on window.SofaConfigUrl (unit-tested via Vitest).
 * Spec: _docs/2026-06-12-sofa-config-url-preload-design.md
 */
(function () {
  "use strict";

  function slugify(text) {
    return String(text || "")
      .toLowerCase()
      .trim()
      .replace(/["']/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Param names accepted in the readable format (also the JSON keys of the
  // compact token). Spec §4.
  var NUMERIC_PARAMS = [
    "width", "depth", "length",
    "chaise_width", "chaise_length", "chaise_left", "chaise_right",
    "bumper_length", "bumper_opening",
    "seats", "backs",
    "seats_width", "seats_length", "seats_right",
    "backs_width", "backs_length", "backs_right", "backs_bumper",
  ];
  var STRING_PARAMS = ["corner", "armless", "fabric", "firmness", "legs"];

  function encodeCompact(map) {
    var json = JSON.stringify(map);
    return btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  function decodeCompact(token) {
    try {
      var b64 = String(token || "").replace(/-/g, "+").replace(/_/g, "/");
      if (!b64) return null;
      while (b64.length % 4) b64 += "=";
      var map = JSON.parse(decodeURIComponent(escape(atob(b64))));
      return map && typeof map === "object" && !Array.isArray(map) ? map : null;
    } catch (e) {
      return null;
    }
  }

  // Returns an object with only recognized, well-typed keys — or null if the
  // URL carries no usable configuration.
  function parseConfigFromSearch(search) {
    var qs;
    try {
      qs = new URLSearchParams(search || "");
    } catch (e) {
      return null;
    }

    var raw = qs.has("sofa") ? decodeCompact(qs.get("sofa")) : null;
    if (!raw) {
      raw = {};
      qs.forEach(function (value, key) {
        raw[key] = value;
      });
    }

    var config = {};
    NUMERIC_PARAMS.forEach(function (key) {
      if (raw[key] === null || raw[key] === undefined || raw[key] === "") return;
      var num = parseFloat(raw[key]);
      if (isFinite(num) && num > 0) config[key] = num;
    });
    if (raw.corner !== null && raw.corner !== undefined) {
      var corner = String(raw.corner).toLowerCase();
      if (corner === "left" || corner === "right") config.corner = corner;
    }
    if (raw.armless !== null && raw.armless !== undefined) {
      var armless = String(raw.armless).toLowerCase();
      if (armless === "left" || armless === "right") config.armless = armless;
    }
    ["fabric", "firmness", "legs"].forEach(function (key) {
      if (raw[key]) {
        var slug = slugify(raw[key]);
        if (slug) config[key] = slug;
      }
    });

    return Object.keys(config).length ? config : null;
  }

  function clampToList(value, list) {
    var nums = (list || []).map(Number).filter(isFinite);
    if (!nums.length) return null;
    return nums.reduce(function (best, n) {
      return Math.abs(n - value) < Math.abs(best - value) ? n : best;
    });
  }

  function clampToKeys(value, optionsMap) {
    return clampToList(value, Object.keys(optionsMap || {}));
  }

  function clampToRange(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function nonEmpty(map) {
    return !!map && Object.keys(map).length > 0;
  }

  // Mirrors index.jsx parseConfiguration just enough to detect the one case
  // where two length maps coexist on a product (u-sectional-bumper-chaise
  // renders its length slider from the bumper map — SofaSlider.jsx:131-134).
  function isUSectionalBumperChaise(rawConfiguration) {
    var source = String(rawConfiguration || "")
      .toLowerCase()
      .replace(/[_\-&/]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return (
      source.indexOf("u") !== -1 &&
      source.indexOf("bumper") !== -1 &&
      source.indexOf("chaise") !== -1
    );
  }

  // URL count param → SofaSlider state key (urlPreset is consumed in the
  // slider's useState initializer; invalid counts are ignored by the
  // slider's own validity guards).
  var COUNT_PRESET_KEYS = {
    seats: "seatsState",
    backs: "backPillowsState",
    seats_width: "seatsStateWidth",
    seats_length: "seatsStateLength",
    seats_right: "seatsStateRight",
    backs_width: "backPillowsStateWidth",
    backs_length: "backPillowsStateLength",
    backs_right: "backPillowsStateRight",
    backs_bumper: "backPillowsStateBumper",
  };

  /**
   * Mutates sofaData.defaultDimensions per the URL config (clamped to valid
   * values) and attaches sofaData.urlPreset for fields SofaSlider cannot
   * derive from defaultDimensions. Must run before the React slider mounts.
   */
  function applyConfigToSliderData(config, sofaData, rawConfiguration) {
    if (!config || !sofaData || !sofaData.defaultDimensions) return;
    var dims = sofaData.defaultDimensions;
    var chaiseOptions = sofaData.chaiseOptions || {};
    var bumperOptions = sofaData.bumperOptions || {};
    var preset = {};

    if (config.width != null) {
      if (nonEmpty(sofaData.wedgeSectionalWidthOptions)) {
        dims.width = clampToKeys(config.width, sofaData.wedgeSectionalWidthOptions);
      } else if (nonEmpty(sofaData.widthOptions)) {
        dims.width = clampToKeys(config.width, sofaData.widthOptions);
      } else if (nonEmpty(sofaData.uSectionalWidthOptions)) {
        dims.width = clampToKeys(config.width, sofaData.uSectionalWidthOptions);
      }
      if (nonEmpty(sofaData.uSectionalWidthOptions)) {
        dims.uSectionalWidth = clampToKeys(config.width, sofaData.uSectionalWidthOptions);
      }
    }

    if (config.depth != null && nonEmpty(sofaData.depthOptions)) {
      dims.depth = clampToKeys(config.depth, sofaData.depthOptions);
    }

    if (config.length != null) {
      if (nonEmpty(sofaData.wedgeSectionalLengthOptions)) {
        dims.length = clampToKeys(config.length, sofaData.wedgeSectionalLengthOptions);
      } else if (nonEmpty(sofaData.lengthOptions)) {
        dims.length = clampToKeys(config.length, sofaData.lengthOptions);
      }
      if (nonEmpty(sofaData.uSectionalLengthOptions)) {
        var lengthMap =
          isUSectionalBumperChaise(rawConfiguration) && nonEmpty(sofaData.bumperLengthOptions)
            ? sofaData.bumperLengthOptions
            : sofaData.uSectionalLengthOptions;
        dims.uSectionalLength = clampToKeys(config.length, lengthMap);
      }
    }

    if (config.chaise_length != null && (chaiseOptions.lengths || []).length) {
      dims.chaiseLength = clampToList(config.chaise_length, chaiseOptions.lengths);
    }
    if (config.chaise_left != null) {
      var leftList = chaiseOptions.leftLengths || chaiseOptions.lengths || [];
      if (leftList.length) dims.chaiseLengthLeft = clampToList(config.chaise_left, leftList);
    }
    if (config.chaise_right != null) {
      var rightList = chaiseOptions.rightLengths || chaiseOptions.lengths || [];
      if (rightList.length) dims.chaiseLengthRight = clampToList(config.chaise_right, rightList);
    }

    if (config.bumper_length != null && (bumperOptions.lengths || []).length) {
      dims.bumperLength = clampToList(config.bumper_length, bumperOptions.lengths);
    }
    if (config.bumper_opening != null && (bumperOptions.lengths || []).length) {
      preset.bumperOpeningSize = clampToRange(
        config.bumper_opening,
        bumperOptions.minOpeningWidth || 14,
        bumperOptions.maxOpeningWidth || 24
      );
    }

    // Only preset chaise width when the product actually has chaise data —
    // plain/bumper U-sectionals auto-derive chaiseWidth (= depth) and must
    // not be overridden by a stray cross-config param.
    var hasChaiseData =
      (chaiseOptions.lengths || []).length > 0 ||
      chaiseOptions.insideMin != null ||
      chaiseOptions.insideMax != null;
    if (config.chaise_width != null && hasChaiseData) {
      // Entire chaise width = inside width + arm of the current total width
      // (SofaSlider.jsx:182-191).
      var widthMap = nonEmpty(sofaData.widthOptions)
        ? sofaData.widthOptions
        : sofaData.uSectionalWidthOptions || {};
      var entry = widthMap[dims.width];
      var arm = (entry && entry.arm) || 8;
      preset.chaiseWidth = clampToRange(
        config.chaise_width,
        (chaiseOptions.insideMin || 27) + arm,
        (chaiseOptions.insideMax || 34) + arm
      );
    }

    // Which end of a 1-side-armless L-sectional has no arm (absolute left/right).
    if (config.armless) preset.armlessSide = config.armless;

    Object.keys(COUNT_PRESET_KEYS).forEach(function (param) {
      if (config[param] != null && Number.isInteger(config[param])) {
        preset[COUNT_PRESET_KEYS[param]] = config[param];
      }
    });

    if (Object.keys(preset).length) sofaData.urlPreset = preset;
  }

  /**
   * Builds the share param map from the cached sofa:configuration:changed
   * detail plus the DOM's active fabric/firmness/leg selections. When the
   * slider never mounted (fallback mode), detail is null and only
   * corner/fabric/firmness/legs are serialized.
   */
  function buildShareParams(detail, doc) {
    var map = {};
    function put(key, value) {
      if (value === null || value === undefined || value === "") return;
      if (typeof value === "number" && !isFinite(value)) return;
      map[key] = value;
    }

    if (detail) {
      put("width", detail.width);
      put("depth", detail.depth);
      put("corner", detail.direction);
      put("armless", detail.armlessSide);
      put("length", detail.length);
      put("bumper_length", detail.bumperLength);
      put("bumper_opening", detail.bumperOpeningSize);
      put("chaise_width", detail.widthChaise != null ? detail.widthChaise : detail.chaiseWidth);
      put("chaise_length", detail.lengthChaise);
      put("chaise_left", detail.lengthChaiseLeft);
      put("chaise_right", detail.lengthChaiseRight);
      if (typeof detail.seats === "number") put("seats", detail.seats);
      if (typeof detail.backPillows === "number") put("backs", detail.backPillows);
      put("seats_width", detail.seatsWidth);
      put("seats_length", detail.seatsLength);
      put("seats_right", detail.seatsRight);
      put("backs_width", detail.backPillowsWidth);
      put("backs_length", detail.backPillowsLength);
      put("backs_right", detail.backPillowsRight);
      put("backs_bumper", detail.backPillowsBumper);
    } else {
      var checked = doc.querySelector('input[name="Corner"]:checked');
      if (checked) {
        var v = checked.value.toLowerCase();
        if (v.indexOf("left") !== -1) put("corner", "left");
        else if (v.indexOf("right") !== -1) put("corner", "right");
      }
      var checkedArmless = doc.querySelector('input[name="Armless Side"]:checked');
      if (checkedArmless) {
        var a = checkedArmless.value.toLowerCase();
        if (a.indexOf("left") !== -1) put("armless", "left");
        else if (a.indexOf("right") !== -1) put("armless", "right");
      }
    }

    function activeLabel(type) {
      var el = doc.querySelector(
        '[data-selector-type="' + type + '"] .enavi_addon.active'
      );
      return el && el.dataset.productLabel ? slugify(el.dataset.productLabel) : null;
    }
    put("fabric", activeLabel("Fabric"));
    put("firmness", activeLabel("Firmness"));
    put("legs", activeLabel("Legs"));

    return map;
  }

  /**
   * Returns a full share URL. Foreign query params (view, preview_theme_id,
   * variant, ...) are preserved; previous config params are replaced.
   */
  function buildShareUrl(map, currentHref, format) {
    var url = new URL(currentHref);
    NUMERIC_PARAMS.concat(STRING_PARAMS, ["sofa"]).forEach(function (k) {
      url.searchParams.delete(k);
    });
    if (format === "compact") {
      url.searchParams.set("sofa", encodeCompact(map));
    } else {
      Object.keys(map).forEach(function (k) {
        url.searchParams.set(k, map[k]);
      });
    }
    return url.toString();
  }

  function applyRadioSide(name, side, doc) {
    if (!side) return false;
    var applied = false;
    doc.querySelectorAll('input[name="' + name + '"]').forEach(function (radio) {
      if (radio.value.toLowerCase().indexOf(side) !== -1) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
        applied = true;
      }
    });
    return applied;
  }

  function applyCorner(corner, doc) {
    return applyRadioSide("Corner", corner, doc);
  }

  function applyArmless(armless, doc) {
    return applyRadioSide("Armless Side", armless, doc);
  }

  // Clicks the addon in [data-selector-type=<type>] whose slugified
  // data-product-label matches exactly, else by prefix (firmness=medium
  // matches "Medium - Most Popular"). Returns whether a match was found.
  function applyAddon(type, slug, doc) {
    if (!slug) return false;
    var addons = Array.prototype.slice.call(
      doc.querySelectorAll('[data-selector-type="' + type + '"] .enavi_addon')
    );
    function find(test) {
      for (var i = 0; i < addons.length; i++) {
        if (test(slugify(addons[i].dataset.productLabel))) return addons[i];
      }
      return null;
    }
    var target =
      find(function (s) { return s === slug; }) ||
      find(function (s) { return s.indexOf(slug + "-") === 0; });
    if (target && !target.classList.contains("active")) {
      // The addon lives inside a closed accordion whose .box-content has the
      // inert attribute. Per the HTML spec, element.click() on an inert node
      // is a no-op. We lift inert before clicking so handleProductSelection
      // fires (updating price, Cylindo, step-completion), then synchronously
      // restore the closed state before the browser can repaint — the user
      // sees no accordion movement.
      var boxContent = target.closest(".box-content");
      var closedByInert = boxContent && boxContent.hasAttribute("inert");
      if (closedByInert) boxContent.removeAttribute("inert");
      target.click();
      if (closedByInert) {
        boxContent.setAttribute("inert", "");
        boxContent.style.maxHeight = "0";
      }
    }
    return !!target;
  }

  // ---------------------------------------------------------------------------
  // Bootstrap — no-op when the URL has no recognized params.
  // ---------------------------------------------------------------------------

  var state = {
    config: null,
    lastDetail: null,
    fabricApplied: false,
  };

  window.SofaConfigUrl = {
    slugify: slugify,
    encodeCompact: encodeCompact,
    decodeCompact: decodeCompact,
    parseConfigFromSearch: parseConfigFromSearch,
    clampToList: clampToList,
    clampToKeys: clampToKeys,
    clampToRange: clampToRange,
    applyConfigToSliderData: applyConfigToSliderData,
    buildShareParams: buildShareParams,
    buildShareUrl: buildShareUrl,
    applyCorner: applyCorner,
    applyAddon: applyAddon,
    // Used by sofa-builder.js to keep Cylindo defaults from overriding
    // URL-provided selections.
    has: function (key) {
      return !!(state.config && state.config[key] != null);
    },
  };

  function onReady(fn) {
    // Deferred scripts run while readyState === "interactive" (after parsing,
    // before DOMContentLoaded). Checking for "loading" misses this window and
    // would call fn() immediately — before sofa-builder.js has attached its
    // click handlers. Only "complete" reliably means DOMContentLoaded is past.
    if (document.readyState === "complete") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn);
    }
  }

  // Always cache the latest slider configuration for the share button.
  document.addEventListener("sofa:configuration:changed", function (e) {
    state.lastDetail = e.detail;
  });

  // Share button wiring — independent of incoming params.
  onReady(function () {
    var btn = document.getElementById("SofaConfigShareBtn");
    if (!btn) return;
    var copiedTimer;
    function pop() {
      // Restart the one-shot "pop" scale animation on every click
      // (remove + reflow + re-add so it replays even on rapid re-clicks).
      btn.classList.remove("is-popping");
      void btn.offsetWidth;
      btn.classList.add("is-popping");
    }
    function showCopied() {
      // Swap the button to its checkmark + "Link copied" state, then revert
      // after the delay. CSS (.is-copied) handles the icon/label swap.
      btn.classList.add("is-copied");
      clearTimeout(copiedTimer);
      copiedTimer = setTimeout(function () {
        btn.classList.remove("is-copied");
      }, 2000);
    }
    btn.addEventListener("click", function () {
      pop();
      var map = buildShareParams(state.lastDetail, document);
      var href = buildShareUrl(map, window.location.href, btn.dataset.shareFormat);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(href).then(showCopied, function () {
          window.prompt("Copy this link:", href);
        });
      } else {
        window.prompt("Copy this link:", href);
      }
    });
  });

  // ---- incoming configuration ----
  state.config = parseConfigFromSearch(window.location.search);
  if (!state.config) return;

  // 1. Dimensions — mutate sofaSliderData before the React slider mounts.
  //    This script tag sits after the builder markup (which includes the
  //    inline sofa-slider-data script) and executes before DOMContentLoaded,
  //    so the data exists and React has not mounted yet.
  var container = document.getElementById("sofa-size-slider-container");
  if (container) {
    var handle = container.dataset.productHandle;
    var sofaData = handle && window.sofaSliderData && window.sofaSliderData[handle];
    if (sofaData) {
      applyConfigToSliderData(state.config, sofaData, container.dataset.builderConfiguration);
    }
  }

  // 2. Corner / armless-side radios — synchronously, before React reads them
  //    on mount.
  applyCorner(state.config.corner, document);
  applyArmless(state.config.armless, document);

  // 3. Firmness / Legs — after sofa-builder.js attaches its click handlers
  //    (it executes after this script; DOMContentLoaded fires after both).
  onReady(function () {
    applyAddon("Firmness", state.config.firmness, document);
    applyAddon("Legs", state.config.legs, document);
  });

  // 4. Fabric — swatches render asynchronously. The setTimeout lets
  //    sofa-builder.js attach click handlers to freshly rendered cards
  //    (its resetEventListeners runs in the same event dispatch, after us)
  //    before we click. While waiting, show a spinner in the fabric step
  //    heading so there's visible feedback that a selection is pending.
  if (state.config.fabric) {
    var fabricBox = document.querySelector('[data-selector-type="Fabric"]');
    if (fabricBox) {
      fabricBox.classList.add("sofa-fabric-preloading");
      var spinStyle = document.createElement("style");
      spinStyle.textContent =
        "[data-selector-type='Fabric'].sofa-fabric-preloading .completion-icon{display:flex}" +
        "[data-selector-type='Fabric'].sofa-fabric-preloading .completion-icon>*{display:none}" +
        "[data-selector-type='Fabric'].sofa-fabric-preloading .completion-icon::after{" +
        "content:'';width:24px;height:24px;" +
        "border:2px solid rgba(32,32,32,0.15);border-top-color:#202020;" +
        "border-radius:50%;animation:sofaFabricSpin 0.7s linear infinite}" +
        "@keyframes sofaFabricSpin{to{transform:rotate(360deg)}}";
      document.head.appendChild(spinStyle);
    }
    var tryFabric = function () {
      setTimeout(function () {
        if (state.fabricApplied) return;
        state.fabricApplied = applyAddon("Fabric", state.config.fabric, document);
        if (state.fabricApplied && fabricBox) {
          fabricBox.classList.remove("sofa-fabric-preloading");
        }
      }, 0);
    };
    document.addEventListener("pdp-fabric-samples:loaded", tryFabric);
    document.addEventListener("pdp-fabric-samples:updated", tryFabric);
  }
})();
